import TableSkeleton from '@components/layouts/TableSkeleton';
import { useDidMountEffect } from '@hooks/useDidMountEffect';
import usePagination from '@hooks/usePagination';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component';
import Modal from "react-modal";
import { useLocation } from 'react-router-dom';
import customModalStyles from '../styles/customModalStyles';



type GetAllQueryHook<TArg = any, TData = any> = (arg?: TArg) => {
    data?: TData | undefined; // Added the '?' here
    isFetching: boolean;
    refetch: () => void;
};

type PropType<T> = {
    label: string;
    columns: TableColumn<T>[];
    columnNames: string[];
    idToSelect: keyof T;
    useGetAllQuery: GetAllQueryHook;
    keyToSearch: keyof T;
    setSelectedIds: Dispatch<SetStateAction<(string | number)[]>>
}


const PrimaryMultiSelectModal = <T,>({
    label,
    columns,
    columnNames,
    idToSelect,
    useGetAllQuery,
    keyToSearch,
    setSelectedIds
}: PropType<T>) => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const { page, limit, handlePageChange, handlePerRowsChange, resetPage } = usePagination(1, 10);

    const [selectedItems, setSelectedItems] = useState<T[]>([]);

    const showSelectedItem = useMemo((): string => {
        if (selectedItems.length > 1) {
            return selectedItems[0][keyToSearch] + " ...";
        } else if (selectedItems.length === 1) {
            return selectedItems[0][keyToSearch] as string
        } else {
            return ""
        }
    }, [selectedItems, keyToSearch])

    const { data, isFetching, refetch }: any = useGetAllQuery({
        page,
        limit,
        search: debouncedSearch || undefined,
        sales_target_type_id: idToSelect === 'sales_target_id' ? 2 : undefined
    });

    const handleChangeOnRowSelect = (row: T) => {
        let tmp_rows = [...selectedItems];
        const hasRow = tmp_rows.some(t_row => row[idToSelect] === t_row[idToSelect]);
        if (!hasRow) {
            tmp_rows.push(row);
        } else {
            tmp_rows = tmp_rows.filter(t_row => t_row[idToSelect] !== row[idToSelect]);
        }

        setSelectedItems(tmp_rows);
    }

    const isRowSelected = useCallback((rowId: string | number) => {
        return selectedItems.some(pt => pt[idToSelect] === rowId)
    }, [idToSelect, selectedItems]);


    const onSubmit = () => {
        if (keyToSearch === "reference_id") {
            const ids = selectedItems.map(item => item[keyToSearch]) as number[];
            setSelectedIds(ids);
        } else {
            const ids = selectedItems.map(item => item[idToSelect]) as number[];
            setSelectedIds(ids);
        }
        setOpen(false);
    }



    const onClearAll = () => {
        setSelectedIds([]);
        setSelectedItems([]);
        setOpen(false);
        setSearchTerm("");
        resetPage();
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            resetPage();
        }, 500);

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        if (location.state?.reload) {
            refetch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.reload]);

    useDidMountEffect(() => {
        refetch();
    }, [page, limit]);




    return (
        <div className="">
            <div className="flex flex-col " style={{ gridColumn: "span 2" }}>
                < label className="mb-2 text-gray-700 capitalize " >
                    {label}
                </ label >


                <div
                    onClick={() => {
                        setOpen(true);
                    }
                    }
                    className="relative block text-gray-400 cursor-pointer focus-within:text-gray-600 w-full border border-gray-400 rounded-md  "
                >
                    <input
                        type="text"
                        name="Search"
                        id="search"
                        className="px-2  w-full h-[34px]   focus:ring-1 ring-primary focus:outline-none rounded cursor-pointer"
                        placeholder={label}
                        value={showSelectedItem}
                        readOnly
                    />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                        fill="currentColor"
                        className="absolute w-6 h-6 transform -translate-y-1/2 cursor-pointer top-1/2 right-3"
                    >
                        <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"></path>
                    </svg>
                </div>
            </div>
            <Modal
                style={customModalStyles}
                ariaHideApp={false}
                isOpen={open}
                contentLabel={label}
                className={""}

            >
                <div className="relative h-full ">

                    <div className="flex justify-end">
                        <svg
                            onClick={() => setOpen(false)}
                            xmlns="http://www.w3.org/2000/svg"
                            height="20px"
                            viewBox="0 -960 960 960"
                            width="20px"
                            fill="#3e3e3e"
                            className="cursor-pointer"
                        >
                            <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                        </svg>
                    </div>

                    <div className=" px-14">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-[600] uppercase text-primary-dark">
                                {label}
                            </h3>

                            <div className="">
                                <div className="flex justify-end items start w-full ">
                                    <div className="w-80 mb-7">
                                        <label className="relative block text-gray-400 focus-within:text-gray-600">
                                            <input
                                                value={searchTerm}
                                                onChange={(ev) => {
                                                    setSearchTerm(ev.target.value || "")
                                                }}
                                                type="text"
                                                name="Search"
                                                id="search"
                                                placeholder="Search"
                                                className="block w-full px-4 py-[0.65rem] text-[0.80rem] text-gray-500 placeholder-gray-400 bg-white border border-gray-200 rounded-md shadow-sm appearance-none form-input focus:outline-none"
                                            />

                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="h-[410px] pe-5 overflow-auto">
                            <DataTable
                                keyField="sale_target_id"
                                responsive
                                striped
                                pagination
                                paginationPerPage={limit}
                                paginationDefaultPage={page}
                                paginationServer
                                //@ts-ignore
                                paginationTotalRows={data?.paginator?.totalItems}
                                progressPending={isFetching}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                noDataComponent={
                                    <TableSkeleton
                                        columnNames={columnNames}
                                        actionRight
                                        row={10}
                                        col={1}
                                    />
                                }
                                progressComponent={
                                    <TableSkeleton
                                        columnNames={columnNames}
                                        actionRight
                                        row={10}
                                        col={1}
                                    />
                                }
                                //@ts-ignore
                                columns={[
                                    {
                                        // @ts-ignore
                                        selector: (row: any) => (
                                            <input
                                                type="checkbox"
                                                // checked={productTargets.some((d: { id: string | number }) => d.id === row.id)}
                                                checked={isRowSelected(row[idToSelect])}
                                                onChange={() => handleChangeOnRowSelect(row)}
                                            />
                                        ),
                                        ignoreRowClick: true,
                                        allowOverflow: true,
                                        button: true,
                                    },
                                    ...columns
                                ]}
                                //@ts-ignore
                                data={data?.data}
                            />
                        </div>

                        <div className='-mt-5'>
                            <button
                                className=" bg-primary-dark flex justify-end float-right cursor-pointer text-center mt-10 text-white py-2 px-3 rounded text-sm outline outline-[0.5px] outline-gray-300 undefined"
                                onClick={onSubmit}
                            >
                                Add
                            </button>

                            <button
                                className=" bg-red-800 flex justify-end float-right cursor-pointer text-center mt-10 me-4 text-white py-2 px-3 rounded text-sm outline outline-[0.5px] outline-gray-300 undefined"
                                onClick={onClearAll}
                            >
                                Clear All
                            </button>
                        </div>

                    </div>

                </div>
            </Modal>
        </div >
    )
}

export default PrimaryMultiSelectModal